/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useContext, useMemo } from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import * as R from 'ramda';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ChainRounded, MIconMenu } from 'library';
import { format } from 'date-fns';
import { hasAllRoles, userRoles } from 'utils/currentUserRoles';

import { DRAWER_TYPES } from 'utils/constants';
import { AuthContext } from 'authentication/AuthContext';
import { DrawerContext } from 'common/drawer/DrawerContext';
import { PTO_DIALOGUE_TYPES, PTO_STATUS } from '../../utils';

// Components
import StatusChip from '../../status-chip/StatusChip';
import ViewPanel from '../../view-panel/ViewPanel.connector';
import EditPtoPanel from '../../edit-panel/EditPanel.connector';
import CreatePtoWizard from '../../create-wizard/CreateWizard.connector';

import {
  actionMenuButtonDivStyles,
  actionWrapperStyles,
  cardStyles,
  chainIconStyles,
  companyLabelStyles,
  contentStyles,
} from './Card.styles';

const SearchResultCard = ({ data, resetCreatePto, setCurrentPtoDialogueOpen }) => {
  const theme = useTheme();
  const { roles: inputRoles, affiliateRoles } = useContext(AuthContext);
  const roles = [...inputRoles, ...affiliateRoles];

  const { setDrawerComponentProps } = useContext(DrawerContext);

  const {
    ptoNumber,
    status,
    affiliateIsOriginator,
    originator,
    recipient,
    dataCompany,
    transferDate,
    location,
    referenceId,
    product,
    volume,
    custody,
  } = data;

  const lgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const onViewDetails = () =>
    setDrawerComponentProps({
      Component: props => <ViewPanel ptoNumber={ptoNumber} {...props} />,
      resetWizard: resetCreatePto,
      type: DRAWER_TYPES.viewPtoDetails,
    });

  const onEditPto = () =>
    setDrawerComponentProps({
      Component: props => <EditPtoPanel isEdit ptoNumber={ptoNumber} {...props} />,
      resetWizard: resetCreatePto,
      type: DRAWER_TYPES.editPto,
    });

  const onCopyPto = () =>
    setDrawerComponentProps({
      Component: props => <CreatePtoWizard copyPtoNumber={ptoNumber} isCopyToNew {...props} />,
      resetWizard: resetCreatePto,
      type: DRAWER_TYPES.copyPto,
    });

  const openPtoDialog = type =>
    setCurrentPtoDialogueOpen({
      ptoDialogueType: type,
      ptoNumber,
    });

  const acceptOrder = {
    label: 'Accept Order',
    onClick: () => openPtoDialog(PTO_DIALOGUE_TYPES.accept),
    value: 'acceptPto',
  };

  const cancelOrder = {
    label: 'Cancel Order',
    onClick: () => {
      openPtoDialog(PTO_DIALOGUE_TYPES.cancel);
    },
    value: DRAWER_TYPES.cancel,
  };

  const copyOrder = {
    label: 'Copy Order',
    onClick: onCopyPto,
    value: DRAWER_TYPES.copyPto,
  };

  const editOrder = {
    label: 'Edit Order',
    onClick: onEditPto,
    value: DRAWER_TYPES.editPto,
  };

  const rejectOrder = {
    label: 'Reject Order',
    onClick: () => openPtoDialog(PTO_DIALOGUE_TYPES.reject),
    value: 'rejectPto',
  };

  const viewOrder = {
    label: 'View Order',
    onClick: onViewDetails,
    value: DRAWER_TYPES.viewPtoDetails,
  };

  const approvalNeeded = status === PTO_STATUS.approvalNeeded;

  const approvedRejectedOrScheduledAndAffiliateIsOriginator =
    R.includes(status, [PTO_STATUS.approved, PTO_STATUS.rejected, PTO_STATUS.scheduled]) &&
    affiliateIsOriginator;

  const bookedCancelledOrExpiredAndAffiliateIsOriginator =
    R.includes(status, [PTO_STATUS.booked, PTO_STATUS.cancelled, PTO_STATUS.expired]) &&
    affiliateIsOriginator;

  const pendingApprovalOrApprovalNeedAndAffiliateIsOrginator =
    status === PTO_STATUS.pendingApproval ||
    (status === PTO_STATUS.approvalNeeded && affiliateIsOriginator);

  const menuItemsViewOnly = [viewOrder];

  const menuItems = useMemo(
    () => [
      viewOrder,
      ...(approvalNeeded ? [acceptOrder, rejectOrder] : []),
      ...(pendingApprovalOrApprovalNeedAndAffiliateIsOrginator ||
      approvedRejectedOrScheduledAndAffiliateIsOriginator
        ? [editOrder, copyOrder, cancelOrder]
        : []),
      ...(bookedCancelledOrExpiredAndAffiliateIsOriginator ? [copyOrder] : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      approvalNeeded,
      approvedRejectedOrScheduledAndAffiliateIsOriginator,
      bookedCancelledOrExpiredAndAffiliateIsOriginator,
      pendingApprovalOrApprovalNeedAndAffiliateIsOrginator,
    ]
  );

  const isChained = custody === 'Chained';

  const chainedMPL = (
    <Box
      alignItems="center"
      display="flex"
      justifyContent={{ xs: 'flex-start', sm: 'flex-start', lg: 'flex-end' }}
      margin={{ xs: `auto 0 ${theme.spacing(2)}`, sm: '0' }}
      pt={1.5}
      width={{ xs: 'auto', sm: '20%' }}
    >
      {isChained && (
        <>
          <ChainRounded sx={{ ...chainIconStyles(theme) }} />
          <Divider flexItem height="30px" orientation="vertical" />
        </>
      )}
      <Box pl={isChained ? theme.spacing(1) : 0}>
        <Typography align="center" variant="body2">
          {dataCompany}
        </Typography>
      </Box>
    </Box>
  );

  // displays the affiliate, whether the affiliate is the recipient or originator, and status of PTO (left most section)
  const affiliateAndStatus = (
    <Box display="flex" justifyContent="space-between" width={{ xs: '100%', lg: '200px' }}>
      <Box pb={{ xs: 1.5, sm: 0 }} pr={1}>
        <Box sx={{ ...companyLabelStyles(theme) }}>
          <Typography className="css-heap-redact" sx={{ fontWeight: 400 }} variant="caption">
            {affiliateIsOriginator ? 'Recipient' : 'Originator'}
          </Typography>
        </Box>
        <Box p={`${theme.spacing(1)} 0 ${theme.spacing(1.5)}`}>
          <Typography
            className="css-heap-redact"
            color="textPrimary"
            component="span"
            variant="body1"
          >
            {affiliateIsOriginator ? recipient.replace(' -', '') : originator.replace(' -', '')}
          </Typography>
        </Box>
        <StatusChip status={status} />
      </Box>
      {!smUp && chainedMPL}
    </Box>
  );

  const upperSection = (
    <Box
      display="flex"
      flex={2}
      flexDirection={{ xs: 'column', lg: 'row' }}
      height={{ xs: '100%', lg: 'initial' }}
      justifyContent={{ xs: 'space-between', lg: 'initial' }}
      p={{ xs: `${theme.spacing(1.5)} 0 0`, sm: `0 0 0 ${theme.spacing(2)}` }}
      width={{ xs: 'auto', lg: '80%' }}
    >
      <Box display="flex" flexDirection="column" width={{ xs: 'auto', lg: '50%' }}>
        <Typography className="css-heap-redact" sx={{ fontWeight: 400 }} variant="caption">
          Location
        </Typography>
        <Typography className="css-heap-redact" color="textPrimary" variant="body2">
          {location.replace(' -', '')}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" width={{ xs: 'auto', lg: '50%' }}>
        <Typography className="css-heap-redact" sx={{ fontWeight: 400 }} variant="caption">
          Product
        </Typography>
        <Typography className="css-heap-redact" color="textPrimary" variant="body2">
          {`${volume} ${product}`}
        </Typography>
      </Box>
    </Box>
  );

  const totalLowerSection = (
    <Box display="flex" flexDirection={{ xs: 'row', lg: 'row-reverse' }} p={0} width="100%">
      {smUp && chainedMPL}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent={{ xs: 'space-between', sm: 'flex-start' }}
        pl={{ lg: 2 }}
        pt={1.5}
        width={{ xs: '100%', sm: '80%' }}
      >
        <Box
          alignItems={{ xs: 'initial', sm: 'center' }}
          display="flex"
          pb={{ xs: 1.5, sm: 0 }}
          width="100%"
        >
          <Box display="flex" flexDirection="column" width="50%">
            <Typography sx={{ fontWeight: 400 }} variant="caption">
              Transfer Date
            </Typography>
            <Typography color="textPrimary" sx={{ fontWeight: 400 }} variant="caption">
              {format(new Date(transferDate), 'MMMM d, yyyy')}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography sx={{ fontWeight: 400 }} variant="caption">
              PTO Number
            </Typography>
            <Typography color="textPrimary" sx={{ fontWeight: 400 }} variant="caption">
              {ptoNumber}
            </Typography>
          </Box>
        </Box>
        <Box alignItems={{ xs: 'initial', sm: 'center' }} display="flex" width="100%">
          <Box display="flex" flexDirection="column" pb={{ xs: 1.5, sm: 0 }} width="50%">
            <Typography sx={{ fontWeight: 400 }} variant="caption">
              Reference ID
            </Typography>
            <Typography color="textPrimary" sx={{ fontWeight: 400 }} variant="caption">
              {`${referenceId || '--'}`}
            </Typography>
          </Box>
          <Box className="css-heap-redact" display="flex" flexDirection="column">
            <Typography sx={{ fontWeight: 400 }} variant="caption">
              {affiliateIsOriginator ? 'Originator' : 'Recipient'}
            </Typography>
            <Typography color="textPrimary" sx={{ fontWeight: 400 }} variant="caption">
              {affiliateIsOriginator ? originator.replace(' -', '') : recipient.replace(' -', '')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Card elevation={0} sx={{ ...cardStyles(theme) }}>
      <Box sx={{ ...actionWrapperStyles(theme), ...actionMenuButtonDivStyles(theme) }}>
        <MIconMenu
          height="20"
          items={
            hasAllRoles([userRoles.PRODUCT_OWNERSHIP_MANAGE, userRoles.INVENTORY_OWNER], roles)
              ? menuItems
              : menuItemsViewOnly
          }
        />
      </Box>
      <CardContent sx={{ ...contentStyles(theme) }}>
        <Box display="flex" flex={1} flexDirection="column">
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} width="100%">
            {affiliateAndStatus}
            <Divider flexItem={smUp} orientation={smUp ? 'vertical' : 'horizontal'} />
            <Box flex={{ lg: '1 1 80%' }}>
              {upperSection}
              {lgUp && totalLowerSection}
            </Box>
          </Box>
          {!lgUp && totalLowerSection}
        </Box>
      </CardContent>
    </Card>
  );
};

SearchResultCard.propTypes = {
  data: shape({
    affiliateIsOriginator: bool.isRequired,
    custody: string.isRequired,
    dataCompany: string.isRequired,
    location: string.isRequired,
    originator: string.isRequired,
    product: string.isRequired,
    ptoNumber: number.isRequired,
    recipient: string.isRequired,
    referenceId: string,
    status: string.isRequired,
    transferDate: string.isRequired,
    volume: string.isRequired,
  }).isRequired,
  resetCreatePto: func.isRequired,
  setCurrentPtoDialogueOpen: func.isRequired,
};

export default SearchResultCard;
